import { sapNotesStatusMap } from 'components/SapNotesStatus/SapNoteStatus';
import Status from 'components/Status/Status';
import dayjs from 'dayjs';
import { Action, CaseListItem } from 'queries/cases/types';
import { useTranslation } from 'react-i18next';
import { EDateFormats } from 'utils';

const EMPTY_VALUE = '-';

interface ActionsDetailsSkeletonProps {
  name: string;
  action_status?: string;
  external_reference?: string;
  external_reference_code?: string | JSX.Element;
  isLabel?: boolean;
}

const ActionsDetailsSkeleton = ({
  name,
  action_status,
  external_reference,
  external_reference_code,
  isLabel = false,
}: ActionsDetailsSkeletonProps) => {
  const { t } = useTranslation();
  const labelClass = isLabel ? 'has-text-weight-bold' : '';
  return (
    <div className="columns mb-0">
      <div className={`column is-half ${labelClass} pt-0 pb-4`}>{name}</div>
      <div className={`column ${labelClass} pt-0 pb-4 ${action_status}`}>
        {t(`case:actions.status.${action_status}`, {
          defaultValue: action_status ?? t('case:Cases.actionStatus'),
        })}
      </div>
      <div className={`column ${labelClass} pt-0 pl-0 pb-4`}>{external_reference}</div>
      <div className={`column ${labelClass} pt-0 pl-0 pb-4`}>{external_reference_code}</div>
    </div>
  );
};

const ActionDetails = ({ actions }: { actions: Action[] }) => {
  const { t } = useTranslation();
  if (!actions || actions.length === 0) {
    return (
      <ActionsDetailsSkeleton
        name="None"
        action_status="-"
        external_reference="-"
        external_reference_code="-"
      />
    );
  }

  return actions.map((action, index) => {
    const externalReference = action?.external_reference ?? {};
    const externalType = externalReference.type;
    const externalCode = externalReference.code;
    const externalStatus = externalReference.status;
    const sapStatus =
      sapNotesStatusMap[externalStatus as keyof typeof sapNotesStatusMap] ??
      sapNotesStatusMap.NOT_FOUND;

    return (
      <ActionsDetailsSkeleton
        name={`${index + 1}- ${action.name}`}
        action_status={action.status}
        external_reference={t(`components:external_reference.type.${externalType}`, {
          defaultValue: externalType ?? EMPTY_VALUE,
        })}
        external_reference_code={
          <div className="is-flex">
            {externalCode ? (
              <>
                <Status status={sapStatus} />
                <span className="ml-1">{externalCode}</span>
              </>
            ) : (
              EMPTY_VALUE
            )}
          </div>
        }
      />
    );
  });
};

export default function CaseDetails({ cases }: { cases: CaseListItem[] }) {
  const { t } = useTranslation();

  return (
    <>
      {cases.map(
        ({
          actions,
          anomaly_start,
          diagnosis,
          dimension,
          discipline,
          id,
          name,
          owner_mail,
          severity,
          start_date,
        }: CaseListItem) => (
          <div className={`case-details-item ${severity}`} key={id}>
            <div className="columns my-4">
              <div className="column is-half py-0">
                <div>
                  <div className="has-text-weight-bold mb-1">{t('case:common.name')}</div>
                  <div>{name}</div>
                </div>

                <div className="my-4">
                  <div className="has-text-weight-bold mb-1">Detailing</div>
                  <p>{diagnosis}</p>
                </div>
              </div>

              <div className="column is-half py-0">
                <div className="columns">
                  <div className="column pb-0 px-0">
                    <div>
                      <div className="has-text-weight-bold mb-1">{t('case:common.severity')}</div>
                      <p>{t(`case:severity.${severity}`, { defaultValue: EMPTY_VALUE })}</p>
                    </div>

                    <div className="my-4">
                      <div className="has-text-weight-bold mb-1">
                        {t('case:common.anomaly_start')}
                      </div>
                      <p>
                        {anomaly_start
                          ? dayjs(anomaly_start).format(EDateFormats.DDMMYYYY)
                          : EMPTY_VALUE}
                      </p>
                    </div>
                  </div>

                  <div className="column pb-0">
                    <div>
                      <div className="has-text-weight-bold mb-1">{t('case:common.discipline')}</div>
                      <p>{t(`case:discipline.${discipline}`, { defaultValue: EMPTY_VALUE })}</p>
                    </div>

                    <div className="my-4">
                      <div className="has-text-weight-bold mb-1">Case creation</div>
                      <p>
                        {start_date ? dayjs(start_date).format(EDateFormats.DDMMYYYY) : EMPTY_VALUE}
                      </p>
                    </div>
                  </div>

                  <div className="column pb-0 px-0">
                    <div>
                      <div className="has-text-weight-bold mb-1">{t('case:common.owner_mail')}</div>
                      <p>{owner_mail ? owner_mail.split('@')[0] : EMPTY_VALUE} </p>
                    </div>

                    <div className="my-4">
                      <div className="has-text-weight-bold mb-1">{t('case:common.dimension')}</div>
                      <p>{t(`case:dimension.${dimension}`, { defaultValue: EMPTY_VALUE })}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ActionsDetailsSkeleton
              name={t('case:common.actions')}
              external_reference={t('case:common.external_reference')}
              external_reference_code={t('case:common.external_reference_code')}
              isLabel
            />

            <ActionDetails actions={actions} />
          </div>
        ),
      )}
    </>
  );
}
